/**
 * ReviewCard — behavioral port of the @smartbox/ui web component.
 *
 * Displays one pending FileResult with:
 *   - Filename and MIME type
 *   - Overall confidence badge (color-coded)
 *   - Status indicator
 *   - ReconciliationFlag (if confidence < 0.70 or any record below threshold)
 *   - FactList (extracted records)
 *   - Confirm and Discard action buttons
 *
 * The card is the primary unit of the review queue (one per pending file).
 * Action handlers are callbacks so the review screen owns async state
 * (loading, error) rather than duplicating it in every card.
 *
 * NEVER imports @smartbox/ui (mobile CLAUDE.md invariant 1).
 *
 * WO-M0 — design-system refactor. All styling via theme tokens + UI primitives.
 */

import { View, StyleSheet } from 'react-native';
import type { FileResult } from '@beam/api-types';
import { FactList } from './FactList';
import { ReconciliationFlag } from './ReconciliationFlag';
import { Card, Text, Button, Badge } from '../ui';
import { theme } from '../../theme';

interface Props {
  fileResult: FileResult;
  onConfirm: () => void;
  onDiscard: () => void;
  /** True while a confirm or discard API call is in-flight for this card. */
  isActing: boolean;
  /** Error message to display if the last action failed. */
  actionError: string | null;
}

const CONFIDENCE_THRESHOLD = 0.70;

/** Map confidence to the closest status key for Badge. */
function confidenceStatus(confidence: number): 'paid' | 'inProgress' | 'overdue' {
  if (confidence >= 0.90) return 'paid';
  if (confidence >= CONFIDENCE_THRESHOLD) return 'inProgress';
  return 'overdue';
}

export function ReviewCard({
  fileResult,
  onConfirm,
  onDiscard,
  isActing,
  actionError,
}: Props) {
  const { filename, mimeType, confidence, status, extractedRecords } = fileResult;
  const confidencePct = (confidence * 100).toFixed(0) + '%';

  return (
    <Card>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text variant="bodyStrong" numberOfLines={1}>{filename}</Text>
          <Text variant="small" color="secondary">{mimeType}</Text>
        </View>
        <Badge status={confidenceStatus(confidence)} label={confidencePct} />
      </View>

      {/* Status */}
      <View style={styles.statusRow}>
        <View style={styles.statusDot} />
        <Text variant="small" color="secondary" style={styles.statusText}>{status}</Text>
        <Text variant="label" color="secondary">
          {extractedRecords.length}{' '}
          {extractedRecords.length === 1 ? 'record' : 'records'}
        </Text>
      </View>

      {/* Reconciliation warning */}
      <ReconciliationFlag
        fileConfidence={confidence}
        records={extractedRecords}
      />

      {/* Extracted facts */}
      <FactList records={extractedRecords} />

      {/* Action error */}
      {actionError ? (
        <Card style={styles.errorCard}>
          <Text variant="small" color="danger" accessibilityRole="alert">{actionError}</Text>
        </Card>
      ) : null}

      {/* Actions: discard = danger, confirm = primary (per HARD RULE §6) */}
      <View style={styles.actions}>
        <Button
          title="Discard"
          variant="danger"
          size="md"
          loading={isActing}
          disabled={isActing}
          onPress={onDiscard}
          accessibilityLabel={`Discard ${filename}`}
          style={styles.discardBtn}
        />
        <Button
          title="Confirm"
          variant="primary"
          size="md"
          loading={isActing}
          disabled={isActing}
          onPress={onConfirm}
          accessibilityLabel={`Confirm ${filename}`}
          style={styles.confirmBtn}
        />
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing.md,
  },
  headerLeft: {
    flex: 1,
    gap: theme.spacing.xs,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  statusDot: {
    width: theme.spacing.sm,
    height: theme.spacing.sm,
    borderRadius: theme.radius.pill,
    backgroundColor: theme.color.action.primary,
  },
  statusText: {
    flex: 1,
    textTransform: 'capitalize',
  },
  errorCard: {
    backgroundColor: theme.status.overdue.bg,
    borderColor: theme.status.overdue.bg,
  },
  actions: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  discardBtn: {
    flex: 1,
  },
  confirmBtn: {
    flex: 2,
  },
});
